// An image's "Image Structure" in the library preview's ⓘ (spec
// docs/specs/web/library-image-structure.md), as live's image preview shows it
// (`preview-modal-components`, read for behaviour): its analysis running, failed with Try again,
// or its texts and visuals, each with live's icon and label, its text and its placement.
import { ActionIcon, Button, Divider, Group, Loader, Stack, Text, Tooltip, VisuallyHidden } from "@mantine/core";
import { Grid2x2, Pencil } from "lucide-react";
import { useState } from "react";

import type { AdCreativeStructure } from "../../api/generated/model";
import promptError from "../../ui/assets/prompt-error-placeholder.png";
import { ErrorAlert } from "../ErrorAlert";
import { DetailSection } from "./MediaPreview";
import { StructureEditor } from "./StructureEditor";
import { TEXT_ELEMENTS, VISUAL_ELEMENTS, shownElements, type ShownElement } from "./structure";
import type { ImageStructureRead } from "./use-image-structure";
import classes from "./ImageStructure.module.css";

/** Live's words while a picture is analysed, and when its analysis failed (`preview-modal-layout`). */
export const ANALYSING = "Analyzing the image to extract its structure...";
export const ANALYSIS_FAILED = "We couldn't extract the image structure.";

/** Whether ⓘ shows the section: once the read answered with an analysis, or refused. */
export function showsStructure(read: ImageStructureRead): boolean {
  return read.error !== null || (read.structure !== undefined && read.structure !== null);
}

export function ImageStructure(props: { read: ImageStructureRead; teamId: string; brandId: string; assetId: string }) {
  const { read } = props;
  const structure = read.structure;
  // Live's pencil puts the editor in place of the rows (PR 2), until Save or Cancel: the structure
  // it opened on, so a read that fails or answers meanwhile never takes the draft away.
  const [editing, setEditing] = useState<AdCreativeStructure | null>(null);
  if (editing !== null) {
    return (
      <StructureEditor
        teamId={props.teamId}
        brandId={props.brandId}
        assetId={props.assetId}
        structure={editing}
        readError={read.error}
        onDone={() => setEditing(null)}
      />
    );
  }
  let body;
  let action;
  if (read.error !== null) {
    body = <ErrorAlert error={read.error} />;
  } else if (structure === undefined || structure === null) {
    return null;
  } else if (structure.status === "in_progress") {
    body = (
      <Group gap={8} wrap="nowrap">
        <Loader size="xs" />
        <Text size="sm">{ANALYSING}</Text>
      </Group>
    );
  } else if (structure.status === "failed" || structure.structure === null) {
    body = (
      <Stack gap={8} align="flex-start">
        {/* Live's error picture over the words (`preview-modal-layout`, spec decision 20). */}
        <img src={promptError} alt="" className={classes.failedPicture} />
        <Text size="sm">{ANALYSIS_FAILED}</Text>
        <Button size="compact-sm" onClick={read.retry} loading={read.retrying}>
          Try again
        </Button>
        {read.retryError === null ? null : <ErrorAlert error={read.retryError} />}
      </Stack>
    );
  } else {
    const opened = structure.structure;
    action = (
      <ActionIcon variant="subtle" size="sm" aria-label="Edit image structure" onClick={() => setEditing(opened)}>
        <Pencil size={16} />
      </ActionIcon>
    );
    const texts = shownElements(TEXT_ELEMENTS, structure.structure.text_elements);
    const visuals = shownElements(VISUAL_ELEMENTS, structure.structure.visual_style);
    body = (
      <>
        {texts.length === 0 ? null : <Elements title="Text" rows={texts} />}
        {texts.length > 0 && visuals.length > 0 ? <Divider className={classes.divider} /> : null}
        {visuals.length === 0 ? null : <Elements title="Visual" rows={visuals} />}
      </>
    );
  }
  return (
    <DetailSection title="Image Structure" action={action}>
      {body}
    </DetailSection>
  );
}

/** A part of the structure, live's `StructureSection`: each element a label line, its text under it. */
function Elements({ title, rows }: { title: string; rows: readonly ShownElement<string>[] }) {
  return (
    <div className={classes.part}>
      <Text className={classes.partTitle}>{title}</Text>
      <ul className={classes.rows} aria-label={title}>
        {rows.map((row) => (
          <li key={row.element.key} className={classes.row}>
            <span className={classes.label}>
              <row.element.icon size={16} strokeWidth={1.5} aria-hidden />
              {row.element.label}
            </span>
            <Placement placement={row.placement} />
            <span className={classes.value}>{row.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Live's read-only placement: its greyed grid, the placement's words in its tooltip (none: no tooltip). */
function Placement({ placement }: { placement: string }) {
  if (placement === "") {
    return (
      <span className={classes.placement} aria-hidden>
        <Grid2x2 size={16} />
      </span>
    );
  }
  return (
    <Tooltip label={placement} multiline maw={260} withArrow position="top">
      <span className={classes.placement}>
        <Grid2x2 size={16} aria-hidden />
        <VisuallyHidden>{`Placement: ${placement}`}</VisuallyHidden>
      </span>
    </Tooltip>
  );
}
